import PrivateVenueContactEditor from "./PrivateVenueContactEditor";
import PublicVenueFacts from "./PublicVenueFacts";
import { useEffect, useRef, useState } from "react";
import { mutate as mutateSWR } from "swr";

import {
  applyPitchTemplate,
  markPitchContacted,
  openPitch,
  savePitchDraft,
  type PitchState,
  type PitchTemplateFill,
} from "@/api/discover";
import { fetchTemplates, sendLeadOutreach } from "@/api/crm";
import { fetchEffectiveEmailSender, type EmailSenderAccount } from "@/api/emailSettings";
import { TemplateManagerDrawer } from "@/components/email/TemplateManagerDrawer";
import type { EmailTemplate } from "@/types/crm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { templateHtmlToText } from "@/lib/safeHtml";
import { formatDay, formatLocation, friendlyError } from "./discoverCopy";

type Props = {
  artistId: string;
  /** Venue to pitch; null closes the sheet. */
  globalVenueId: string | null;
  venueName?: string | null;
  onClose: () => void;
  onStateChange: (globalVenueId: string, state: "prospect" | "contacted") => void;
};

/** Common CRM registry variables (core/template_variables.py); the Template Manager lists them all. */
export const PITCH_TEMPLATE_VARIABLES = ["artist_name", "venue_name", "venue_city", "contact_name", "artist_website", "artist_epk_url"];

export function buildCopyText(subject: string, body: string): string {
  const trimmedSubject = subject.trim();
  const trimmedBody = templateHtmlToText(body);
  return trimmedSubject ? `Subject: ${trimmedSubject}\n\n${trimmedBody}` : trimmedBody;
}

export default function PitchSheet({ artistId, globalVenueId, venueName, onClose, onStateChange }: Props) {
  const [pitch, setPitch] = useState<PitchState | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipient, setRecipient] = useState("");
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState("");
  const [applying, setApplying] = useState(false);
  const [fill, setFill] = useState<PitchTemplateFill | null>(null);
  const [managerOpen, setManagerOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [markingContacted, setMarkingContacted] = useState(false);
  const [sending, setSending] = useState(false);
  const [effectiveSender, setEffectiveSender] = useState<EmailSenderAccount | null>(null);
  const [templateVersionId, setTemplateVersionId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const copiedTimer = useRef<number | null>(null);
  const sendIdempotencyKey = useRef<string | null>(null);

  useEffect(() => {
    if (!globalVenueId) return;
    let cancelled = false;
    setPitch(null);
    setLoading(true);
    setLoadError(null);
    setFill(null);
    setTemplateId("");
    setActionError(null);
    setSaveMessage(null);
    sendIdempotencyKey.current = null;
    setCopied(false);
    setDirty(false);
    Promise.all([openPitch(globalVenueId, artistId), fetchEffectiveEmailSender().catch(() => null)])
      .then(([state, sender]) => {
        if (cancelled) return;
        setPitch(state);
        setEffectiveSender(sender);
        setSubject(state.draft?.subject ?? "");
        setBody(state.draft?.body ?? "");
        setRecipient(state.draft?.recipient ?? state.contact.email ?? "");
        onStateChange(globalVenueId, state.state);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(friendlyError(err, "We couldn't open this pitch. Please try again."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalVenueId, artistId]);

  const loadTemplates = () =>
    fetchTemplates(true)
      .then((list) => {
        setTemplates(list.filter((template) => template.channel === "email" && (template.versions ?? []).length > 0));
        setTemplatesError(null);
      })
      .catch((err) => setTemplatesError(friendlyError(err, "We couldn't load your templates.")));

  useEffect(() => {
    if (globalVenueId) void loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalVenueId, artistId]);

  useEffect(
    () => () => {
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
    },
    [],
  );

  const hasDraft = Boolean(subject.trim() || body.trim());

  const handleApplyTemplate = async () => {
    if (!globalVenueId || !templateId) return;
    if (hasDraft && !window.confirm("Replace the current subject and pitch with this template?")) return;
    setApplying(true);
    setActionError(null);
    try {
      const result = await applyPitchTemplate(globalVenueId, Number(templateId), artistId);
      setSubject(result.subject);
      setBody(result.body);
      setFill(result);
      setTemplateVersionId(result.template_version_id);
      setDirty(true);
    } catch (err) {
      setActionError(friendlyError(err, "We couldn't use that template. Please try again."));
    } finally {
      setApplying(false);
    }
  };

  const handleCopy = async () => {
    const text = buildCopyText(subject, body);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setActionError("Copy didn't work in this browser. Select the text and copy it manually.");
    }
  };

  const handleSave = async () => {
    if (!globalVenueId) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await savePitchDraft(globalVenueId, { subject, body, recipient: recipient.trim() || null }, artistId);
      setDirty(false);
      setSaveMessage("Draft saved");
    } catch (err) {
      setActionError(friendlyError(err, "We couldn't save your draft. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleMarkContacted = async () => {
    if (!globalVenueId) return;
    setMarkingContacted(true);
    setActionError(null);
    try {
      if (dirty && hasDraft) {
        await savePitchDraft(globalVenueId, { subject, body, recipient: recipient.trim() || null }, artistId);
        setDirty(false);
      }
      const state = await markPitchContacted(globalVenueId, artistId);
      setPitch(state);
      onStateChange(globalVenueId, state.state);
    } catch (err) {
      setActionError(friendlyError(err, "We couldn't update this venue. Please try again."));
    } finally {
      setMarkingContacted(false);
    }
  };

  const unresolvedPlaceholders = Array.from(
    `${subject}\n${body}`.matchAll(/\{\{\s*(?:UNKNOWN:)?([^{}]+?)\s*\}\}|\[([a-zA-Z][a-zA-Z0-9_.-]*)\]/g),
  );
  const senderConnected = effectiveSender?.verification_status === "connected" && Boolean(effectiveSender.email_address);
  const recipientMatchesContact = Boolean(
    pitch?.contact.email && recipient.trim().toLowerCase() === pitch.contact.email.trim().toLowerCase(),
  );
  const canSend = senderConnected && recipientMatchesContact && Boolean(subject.trim() && body.trim()) && unresolvedPlaceholders.length === 0;

  const handleSend = async () => {
    if (!pitch || !canSend || !effectiveSender?.email_address) return;
    const from = effectiveSender.from_name
      ? `${effectiveSender.from_name} <${effectiveSender.email_address}>`
      : effectiveSender.email_address;
    if (!window.confirm(`Send this email from ${from} to ${recipient.trim()}?`)) return;
    setSending(true);
    setActionError(null);
    try {
      const idempotencyKey = sendIdempotencyKey.current
        ?? (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${pitch.lead_id}`);
      sendIdempotencyKey.current = idempotencyKey;
      const result = await sendLeadOutreach(pitch.lead_id, {
        subject: subject.trim(),
        body: body.trim(),
        recipient: recipient.trim(),
        template_version_id: templateVersionId,
        idempotency_key: idempotencyKey,
      });
      setDirty(false);
      if (result.crm_sync?.status === "pending") {
        setSaveMessage("Email accepted, but Booking could not update yet. Do not resend; retrying this action will only repair the Booking stage.");
      } else {
        const contactedAt = new Date().toISOString();
        setPitch((current) => current ? { ...current, state: "contacted", contacted_at: contactedAt } : current);
        setSaveMessage(
          result.crm_sync?.status === "preserved_later_stage"
            ? "Email sent. The existing later Booking stage was preserved."
            : "Email sent. Booking moved to Outreach.",
        );
        if (globalVenueId) onStateChange(globalVenueId, "contacted");
        void mutateSWR(
          (key) => Array.isArray(key) && key[0] === "booking/leads" && key[1] === artistId,
          undefined,
          { revalidate: true },
        );
      }
    } catch (err) {
      sendIdempotencyKey.current = null;
      setActionError(friendlyError(err, "We couldn't send this email. Check the recipient and sender settings, then try again."));
    } finally {
      setSending(false);
    }
  };

  const venue = pitch?.venue;
  const contacted = pitch?.state === "contacted";

  return (
    <Sheet open={Boolean(globalVenueId)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="max-h-[90dvh] w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Pitch {venue?.name ?? venueName ?? "venue"}</SheetTitle>
          <SheetDescription>
            {venue
              ? `${formatLocation([venue.city, venue.region]) || "Location unknown"}${
                  venue.capacity ? ` · ${venue.capacity.toLocaleString()} capacity` : ""
                }`
              : "Prepare your outreach."}
          </SheetDescription>
        </SheetHeader>

        {loading && <p className="mt-4 text-sm text-muted-foreground">Opening pitch…</p>}
        {loadError && (
          <p role="alert" className="mt-4 text-sm text-destructive">
            {loadError}
          </p>
        )}

        {pitch && (
          <div className="mt-4 space-y-4">
            <div
              className={`rounded-lg px-3 py-2 text-sm ${
                contacted ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-muted/50 text-muted-foreground"
              }`}
              data-testid="pitch-state"
            >
              {contacted
                ? `Contacted${pitch.contacted_at ? ` on ${formatDay(pitch.contacted_at)}` : ""}`
                : "Not contacted yet. Send your pitch however you like, then mark it contacted."}
            </div>

            {globalVenueId && <PrivateVenueContactEditor artistId={artistId} globalVenueId={globalVenueId} initialContact={pitch.private_contact} onSaved={(contact) => {
              setPitch(current => current ? {
                ...current,
                private_contact: contact,
                contact: { name: contact.name, email: contact.email },
              } : current);
              if (!pitch.draft?.recipient && !dirty) {
                const emails = (pitch.venue.facts ?? []).filter(fact => fact.fact_type === "booking_email");
                const general = (pitch.venue.facts ?? []).filter(fact => fact.fact_type === "general_email");
                const publicEmail = pitch.venue.preferred_route?.kind === "booking_url" ? null : emails.length === 1 ? emails[0].value : emails.length === 0 && general.length === 1 ? general[0].value : null;
                setRecipient(contact.email ?? publicEmail ?? "");
              }
            }} />}
            <h3 className="font-semibold">Public booking</h3>
            {pitch.venue.preferred_route?.kind === "booking_url" && pitch.venue.preferred_route.values.map(url => <a key={url} href={url} target="_blank" rel="noreferrer" className="block rounded-md bg-primary px-4 py-3 font-semibold text-primary-foreground">USE BOOKING FORM ↗</a>)}
            {pitch.venue.preferred_route?.kind === "contact_url" && pitch.venue.preferred_route.values.map(url => <a key={url} href={url} target="_blank" rel="noreferrer" className="block rounded-md border px-4 py-3 font-semibold text-primary">OPEN CONTACT PAGE ↗</a>)}
            {pitch.venue.preferred_route?.kind === "general_email" && <p className="text-sm text-muted-foreground">{pitch.venue.preferred_route.label === "booking_role" ? "Booking contact · the venue books through this address." : pitch.venue.preferred_route.label === "likely_books_shows" ? "Venue inbox · likely books shows here (no dedicated booking contact listed)." : "General email fallback · no known booking contact."}</p>}
            {pitch.venue.preferred_route?.kind === "none" && <p className="text-sm text-muted-foreground">No known booking contact.</p>}
            <PublicVenueFacts facts={pitch.venue.facts ?? []} />
            <label className="block text-xs text-muted-foreground">
              Draft recipient (optional)
              <select aria-label="Choose draft recipient" className="mt-1 block w-full rounded-md border bg-background p-2" value={recipient} onChange={event => { setRecipient(event.target.value); setDirty(true); }}>
                <option value="">Choose an email or enter a draft-only override</option>
                {pitch.private_contact?.email && <option value={pitch.private_contact.email}>{pitch.private_contact.name ?? pitch.private_contact.email} — Your contact</option>}
                {(pitch.venue.facts ?? []).filter(fact => fact.fact_type === "booking_email" || fact.fact_type === "general_email").map(fact => <option key={`${fact.fact_type}-${fact.value}`} value={fact.value}>{fact.value} — {fact.fact_type === "booking_email" ? "Public booking" : "Public general"}</option>)}
                {recipient && recipient !== pitch.private_contact?.email && !(pitch.venue.facts ?? []).some(fact => fact.value === recipient) && <option value={recipient}>{recipient} — Draft override</option>}
              </select>
              <Input
                className="mt-1"
                placeholder="Email for this draft only"
                value={recipient}
                onChange={(event) => {
                  setRecipient(event.target.value);
                  setDirty(true);
                }}
                aria-label="Recipient"
              />
              <span className="mt-1 block">Draft-only override. Choose Save draft to keep it; Save contact above remembers your venue contact.</span>
            </label>

            <div className="space-y-2" data-testid="pitch-templates">
              <label className="block text-xs text-muted-foreground">
                Start from one of your templates
                <div className="mt-1 flex gap-2">
                  <select
                    aria-label="Choose template"
                    className="block w-full rounded-md border bg-background p-2"
                    value={templateId}
                    onChange={(event) => setTemplateId(event.target.value)}
                  >
                    <option value="">{templates.length ? "Choose a template" : "No saved templates yet"}</option>
                    {templates.map((template) => (
                      <option key={template.id} value={String(template.id)}>
                        {template.name}{template.versions?.[0]?.metadata?.booking_template_kind === pitch.recommended_template_kind ? " (Recommended)" : ""}
                      </option>
                    ))}
                  </select>
                  <Button variant="outline" onClick={() => void handleApplyTemplate()} disabled={!templateId || applying}>
                    {applying ? "Filling…" : "Use template"}
                  </Button>
                </div>
              </label>
              <Button variant="link" className="h-auto p-0 text-xs" onClick={() => setManagerOpen(true)}>
                Manage templates
              </Button>
              {templatesError && <p className="text-xs text-destructive">{templatesError}</p>}
              {fill && fill.missing.length > 0 && (
                <p role="status" className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200" data-testid="pitch-missing">
                  Fill these in by hand before sending — we don't have them yet:{" "}
                  {fill.missing.map((name) => `{{${name}}}`).join(", ")}.
                </p>
              )}
              {fill && fill.unknown.length > 0 && (
                <p role="status" className="text-xs text-muted-foreground" data-testid="pitch-unknown">
                  Not a supported variable (shown as {"{{UNKNOWN:…}}"}): {fill.unknown.map((name) => `{{${name}}}`).join(", ")}.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Variables: {PITCH_TEMPLATE_VARIABLES.map((name) => `{{${name}}}`).join(" ")}
              </p>
            </div>

            <label className="block text-xs text-muted-foreground">
              Subject
              <Input
                className="mt-1"
                value={subject}
                onChange={(event) => {
                  setSubject(event.target.value);
                  setDirty(true);
                }}
                aria-label="Subject"
              />
            </label>
            <label className="block text-xs text-muted-foreground">
              Pitch
              <RichTextEditor
                className="mt-1"
                value={body}
                onChange={(value) => {
                  setBody(value);
                  setDirty(true);
                }}
                placeholder="Choose a template or write your own."
                ariaLabel="Pitch body"
                minHeight={220}
              />
            </label>

            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => void handleSend()} disabled={!canSend || sending}>
                {sending ? "Sending…" : "Send email"}
              </Button>
              <Button variant="secondary" onClick={() => void handleCopy()} disabled={!hasDraft}>
                {copied ? "Copied" : "Copy pitch"}
              </Button>
              <Button variant="outline" onClick={() => void handleSave()} disabled={!hasDraft || saving || !dirty}>
                {saving ? "Saving…" : "Save draft"}
              </Button>
              {saveMessage && !dirty && <span className="text-xs text-muted-foreground">{saveMessage}</span>}
            </div>
            {!senderConnected && (
              <p className="text-xs text-amber-700 dark:text-amber-300">Connect and verify an email sender in Settings before sending.</p>
            )}
            {recipient.trim() && !recipientMatchesContact && (
              <p className="text-xs text-amber-700 dark:text-amber-300">Save this address as the venue contact before sending.</p>
            )}
            {unresolvedPlaceholders.length > 0 && (
              <p className="text-xs text-amber-700 dark:text-amber-300">Resolve all template placeholders before sending.</p>
            )}

            <div className="border-t border-border pt-4">
              {contacted ? (
                <p className="text-sm text-muted-foreground">This venue is in your Booking pipeline as contacted.</p>
              ) : (
                <>
                  <Button onClick={() => void handleMarkContacted()} disabled={markingContacted}>
                    {markingContacted ? "Updating…" : "Mark contacted"}
                  </Button>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Use this after you've actually sent your pitch. It moves the venue to Outreach in Booking.
                  </p>
                </>
              )}
            </div>

            {actionError && (
              <p role="alert" className="text-sm text-destructive">
                {actionError}
              </p>
            )}
          </div>
        )}
      </SheetContent>
      <TemplateManagerDrawer
        open={managerOpen}
        onClose={() => setManagerOpen(false)}
        onTemplatesChanged={() => void loadTemplates()}
        allowSystemTemplates={false}
      />
    </Sheet>
  );
}
